import React from 'react';
import { useDashboard } from '../../contexts/DashboardContext';

const CalendarEventModals = () => {
  const {
    API,
    axios,
    moment,
    proposalNote,
    proposedDate,
    proposedTime,
    rejectReason,
    selectedEvent,
    selectedPendingSubmission,
    setOrderFormSubmissions,
    setProposalNote,
    setProposedDate,
    setProposedTime,
    setRejectReason,
    setSelectedEvent,
    setSelectedPendingSubmission,
    setShowEventDetails,
    setShowProposeTimeModal,
    setShowRejectModal,
    showEventDetails,
    showProposeTimeModal,
    showRejectModal,
    showToast,
  } = useDashboard();

  return (
    <>
        {/* Global Reject Booking Modal - Accessible from both Calendar and Appointments pages */}
        {showRejectModal && selectedPendingSubmission && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="bg-red-50 border-b border-red-200 px-6 py-4 rounded-t-xl">
                <h3 className="text-lg font-semibold text-red-800">Reject Booking Request</h3>
                <p className="text-sm text-red-600 mt-1">
                  Rejecting booking from {selectedPendingSubmission.client_name}
                </p>
              </div>
              
              <div className="p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Rejection (will be sent to client)
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  placeholder="e.g., The requested time slot is no longer available, Photographer unavailable on this date..."
                />
              </div>
              
              <div className="bg-gray-50 px-6 py-4 rounded-b-xl flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      await axios.put(`${API}/order-form-submissions/${selectedPendingSubmission.id}/reject`, {
                        reason: rejectReason
                      });
                      setOrderFormSubmissions(prev => 
                        prev.map(s => s.id === selectedPendingSubmission.id ? { ...s, status: 'rejected' } : s)
                      );
                      setShowRejectModal(false);
                      setSelectedPendingSubmission(null);
                      showToast('Booking request rejected. Client will be notified.', 'success');
                    } catch (err) {
                      showToast('Failed to reject booking', 'error');
                    }
                  }}
                  className="px-6 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors font-medium"
                >
                  Send Rejection
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Global Propose New Time Modal - Accessible from both Calendar and Appointments pages */}
        {showProposeTimeModal && selectedPendingSubmission && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="bg-blue-50 border-b border-blue-200 px-6 py-4 rounded-t-xl">
                <h3 className="text-lg font-semibold text-blue-800">Propose New Time</h3>
                <p className="text-sm text-blue-600 mt-1">
                  Suggest an alternative time for {selectedPendingSubmission.client_name}
                </p>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Original time display */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Originally Requested</p>
                  <p className="font-medium text-gray-900">
                    {moment(selectedPendingSubmission.scheduled_date).format('ddd, MMM D, YYYY')} at {selectedPendingSubmission.scheduled_time}
                  </p>
                </div>
                
                {/* New date picker */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Proposed Date
                  </label>
                  <input
                    type="date"
                    value={proposedDate}
                    onChange={(e) => setProposedDate(e.target.value)}
                    min={moment().format('YYYY-MM-DD')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                {/* New time picker */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Proposed Time
                  </label>
                  <select
                    value={proposedTime}
                    onChange={(e) => setProposedTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a time...</option>
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = Math.floor(i / 2) + 8; // 8 AM to 7 PM
                      const minute = (i % 2) * 30;
                      if (hour > 19) return null;
                      const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                      const time12 = moment(time24, 'HH:mm').format('h:mm A');
                      return (
                        <option key={time24} value={time24}>{time12}</option>
                      );
                    })}
                  </select>
                </div>
                
                {/* Note */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note to Client (optional)
                  </label>
                  <textarea
                    value={proposalNote}
                    onChange={(e) => setProposalNote(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="e.g., This time works better with the photographer's schedule..."
                  />
                </div>
              </div>
              
              <div className="bg-gray-50 px-6 py-4 rounded-b-xl flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowProposeTimeModal(false);
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!proposedDate || !proposedTime) {
                      showToast('Please select both date and time', 'error');
                      return;
                    }
                    try {
                      await axios.put(`${API}/order-form-submissions/${selectedPendingSubmission.id}/propose-new-time`, {
                        proposed_date: proposedDate,
                        proposed_time: proposedTime,
                        note: proposalNote
                      });
                      setOrderFormSubmissions(prev => 
                        prev.map(s => s.id === selectedPendingSubmission.id ? { ...s, status: 'new_time_proposed' } : s)
                      );
                      setShowProposeTimeModal(false);
                      setSelectedPendingSubmission(null);
                      showToast('New time proposed! Client will be notified.', 'success');
                    } catch (err) {
                      showToast('Failed to send proposal', 'error');
                    }
                  }}
                  disabled={!proposedDate || !proposedTime}
                  className="px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Send Proposal
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Google Calendar Event Details Modal */}
        {showEventDetails && selectedEvent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[150]">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-[95vw] md:max-w-2xl">
              <div className="px-6 py-4 bg-green-700 text-white rounded-t-xl flex justify-between items-center">
                <h3 className="text-lg font-semibold">Google Calendar Event</h3>
                <button
                  onClick={() => {
                    setShowEventDetails(false);
                    setSelectedEvent(null);
                  }}
                  className="text-white hover:text-gray-200 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Event Title</label>
                  <p className="text-gray-900">{selectedEvent.title}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Start Time</label>
                    <p className="text-gray-900">{moment(selectedEvent.start).format('MMM D, YYYY h:mm A')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">End Time</label>
                    <p className="text-gray-900">{moment(selectedEvent.end).format('MMM D, YYYY h:mm A')}</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Team Member</label>
                  <p className="text-gray-900">{selectedEvent.team_member_name}</p>
                </div>
                
                {selectedEvent.description && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                    <p className="text-gray-900 whitespace-pre-wrap">{selectedEvent.description}</p>
                  </div>
                )}
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-300">
                    <strong>Note:</strong> This event is synced from Google Calendar and can only be edited in Google Calendar.
                  </p>
                </div>
              </div>
              
              <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end">
                <button
                  onClick={() => {
                    setShowEventDetails(false);
                    setSelectedEvent(null);
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
    </>
  );
};

export default CalendarEventModals;
